import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  IPmAdminRow,
  useAdminPmUpdate,
} from '../../hooks/reactQuerys/ProcessManagerAdminQueries';
import { useAlert } from '../../context/AlertContext';

interface IProps {
  open: boolean;
  row: IPmAdminRow | null;
  onClose: () => void;
}

// Stammdaten aus DB-Tabelle ``ProcessManagerState``.
const STATE_LABELS: Record<number, string> = {
  0: 'Neu',
  1: 'Offen',
  2: 'Übermittelt',
  3: 'Eingetragen',
};

/**
 * Roh-Editor für einen einzelnen ProcessManager-Eintrag. Erlaubt direkt:
 *   - data (Multi-Line JSON-Textfeld mit Validierung beim Speichern)
 *   - Status (idProcessManagerState)
 *   - Tries (manuelle Korrektur)
 *   - LastTry_at / Closed_at / DateToCheck (ISO-Strings, leer = NULL)
 *
 * Automations werden read-only mit ihren idProcessManagerAutomationType
 * + data-Snippets angezeigt — Bearbeiten passiert weiterhin im PM-Tab
 * des jeweiligen Vorgangs (oder via Backend), damit hier kein zweites
 * Edit-Form für jede Automation-Variante gepflegt werden muss.
 */
export const PmAdminEditDialog: React.FC<IProps> = ({ open, row, onClose }) => {
  const { showAlert } = useAlert();
  const { updatePm, isUpdatingPm } = useAdminPmUpdate();

  const [dataText, setDataText] = useState('');
  const [state, setState] = useState<number>(1);
  const [tries, setTries] = useState<string>('0');
  const [exitCode, setExitCode] = useState<string>('');
  const [lastTryAt, setLastTryAt] = useState<string>('');
  const [closedAt, setClosedAt] = useState<string>('');
  const [dateToCheck, setDateToCheck] = useState<string>('');
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!row) return;
    // Pretty-Print, damit der User es bequem editieren kann.
    let pretty = row.data ?? '';
    if (pretty) {
      try {
        pretty = JSON.stringify(JSON.parse(pretty), null, 2);
      } catch {
        // Falls invalid JSON gespeichert ist, zeigen wir es roh —
        // der User kann es dann korrigieren.
      }
    }
    setDataText(pretty);
    setState(row.idProcessManagerState ?? 1);
    setTries(String(row.Tries ?? 0));
    setExitCode(
      row.ExitCodeLastTry === null || row.ExitCodeLastTry === undefined
        ? ''
        : String(row.ExitCodeLastTry)
    );
    setLastTryAt(row.LastTry_at ?? '');
    setClosedAt(row.Closed_at ?? '');
    setDateToCheck(row.DateToCheck ?? '');
    setValidationError(null);
  }, [row]);

  if (!row) return null;

  const handleSave = async () => {
    setValidationError(null);
    // data-Validierung (JSON oder leer)
    let dataPayload: string | null = null;
    const trimmed = dataText.trim();
    if (trimmed !== '') {
      try {
        // Round-Trip → minified speichern, damit Whitespace im JSON nicht
        // bei jedem Edit Diff erzeugt.
        dataPayload = JSON.stringify(JSON.parse(trimmed));
      } catch (e) {
        setValidationError(
          e instanceof Error
            ? `data ist kein gültiges JSON: ${e.message}`
            : 'data ist kein gültiges JSON.'
        );
        return;
      }
    }

    try {
      await updatePm({
        id: row.idProcessManager,
        body: {
          data: dataPayload,
          idProcessManagerState: state,
          Tries: parseInt(tries, 10) || 0,
          ExitCodeLastTry:
            exitCode.trim() === '' ? null : parseInt(exitCode, 10),
          LastTry_at: lastTryAt.trim() === '' ? null : lastTryAt,
          Closed_at: closedAt.trim() === '' ? null : closedAt,
          DateToCheck: dateToCheck.trim() === '' ? null : dateToCheck,
        },
      });
      showAlert({
        text: `PM ${row.idProcessManager} aktualisiert.`,
        severity: 'success',
      });
      onClose();
    } catch (e) {
      showAlert({
        text:
          e instanceof Error
            ? `Update fehlgeschlagen: ${e.message}`
            : 'Update fehlgeschlagen.',
        severity: 'error',
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography variant='h6' component='span'>
            PM #{row.idProcessManager} bearbeiten
          </Typography>
          <Chip
            size='small'
            variant='outlined'
            label={`Typ ${row.idProcessManagerType} – ${row.ProcessManagerType ?? '?'}`}
          />
          {row.idLegalTransaction && (
            <Chip
              size='small'
              variant='outlined'
              label={`Vorgang #${row.idLegalTransaction}`}
            />
          )}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction='row' spacing={2}>
            <TextField
              select
              label='Status'
              value={state}
              onChange={e => setState(Number(e.target.value))}
              sx={{ minWidth: 180 }}
            >
              {[0, 1, 2, 3].map(s => (
                <MenuItem key={s} value={s}>
                  {s} · {STATE_LABELS[s]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label='Tries'
              value={tries}
              onChange={e => setTries(e.target.value.replace(/[^0-9]/g, ''))}
              sx={{ minWidth: 100 }}
              inputProps={{ inputMode: 'numeric' }}
            />
            <TextField
              label='ExitCodeLastTry (leer = NULL)'
              value={exitCode}
              onChange={e =>
                setExitCode(e.target.value.replace(/[^0-9-]/g, ''))
              }
              sx={{ minWidth: 200 }}
              inputProps={{ inputMode: 'numeric' }}
            />
          </Stack>
          <Stack direction='row' spacing={2}>
            <TextField
              label='DateToCheck (YYYY-MM-DD)'
              value={dateToCheck}
              onChange={e => setDateToCheck(e.target.value)}
              sx={{ flex: 1 }}
              helperText='Leer lassen für NULL'
            />
            <TextField
              label='LastTry_at (ISO)'
              value={lastTryAt}
              onChange={e => setLastTryAt(e.target.value)}
              sx={{ flex: 1 }}
              helperText='z. B. 2026-06-01 08:30:00 oder leer = NULL (Re-Trigger)'
            />
            <TextField
              label='Closed_at (ISO)'
              value={closedAt}
              onChange={e => setClosedAt(e.target.value)}
              sx={{ flex: 1 }}
              helperText='Leer = NULL'
            />
          </Stack>
          <Box>
            <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
              data (JSON)
            </Typography>
            <TextField
              value={dataText}
              onChange={e => setDataText(e.target.value)}
              multiline
              minRows={8}
              maxRows={20}
              fullWidth
              inputProps={{
                spellCheck: false,
                style: { fontFamily: 'monospace', fontSize: 13 },
              }}
            />
            {validationError && (
              <Alert severity='error' sx={{ mt: 1 }}>
                {validationError}
              </Alert>
            )}
          </Box>

          {row.Automations && row.Automations.length > 0 && (
            <Box>
              <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
                Automationen ({row.Automations.length})
              </Typography>
              <Stack spacing={0.5}>
                {row.Automations.map(a => (
                  <Box
                    key={`auto-${a.idProcessManagerAutomation}`}
                    sx={{
                      px: 1,
                      py: 0.5,
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: 12,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                    }}
                  >
                    <strong>#{a.idProcessManagerAutomation}</strong> · Type{' '}
                    {a.idProcessManagerAutomationType} · {a.data ?? '∅'}
                  </Box>
                ))}
              </Stack>
              <Typography variant='caption' color='text.secondary'>
                Read-only — Automationen werden im Vorgang oder direkt im
                Backend bearbeitet.
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isUpdatingPm}>
          Abbrechen
        </Button>
        <Button
          variant='contained'
          onClick={handleSave}
          disabled={isUpdatingPm}
          startIcon={isUpdatingPm ? <CircularProgress size={16} /> : undefined}
        >
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};
